/* WebComponentImageGalleryItem component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { OpenEvent, CloseEvent, LinkClickEvent } from './events';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { Component, Element, h, Host, State, Event, EventEmitter } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars

@Component({
  tag: 'cwc-image-gallery-item',
  styleUrl: './styles/cwc-image-gallery-item.scss',
  shadow: true,
})
export class ImageGalleryItemComponent {
  @Element() element: HTMLElement;

  @State() overlayIsOpen: boolean = false;
  @State() cssCustomProperties = {};

  private resizeObserver: ResizeObserver;

  /**
   * Event thrown when the overlay has opened
   *
   * @docs
   * @event on:image-gallery-item:opened
   */
  @Event({
    eventName: 'on:image-gallery-item:opened',
  })
  onImageGalleryItemOpened: EventEmitter<OpenEvent>;

  /**
   * Event thrown when the overlay has closed
   *
   * @docs
   * @event on:image-gallery-item:closed
   */
  @Event({
    eventName: 'on:image-gallery-item:closed',
  })
  onImageGalleryItemClosed: EventEmitter<CloseEvent>;

  /**
   * Event thrown when a content link was clicked
   *
   * @docs
   * @event on:image-gallery:link-clicked
   */
  @Event({
    eventName: 'on:image-gallery:link-clicked',
  })
  onImageGalleryLinkClicked: EventEmitter<LinkClickEvent>;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.resizeObserver = new ResizeObserver(async () => {
      this.cssCustomProperties = {
        '--width': `${this.element.offsetWidth}px`,
        '--height': `${this.element.offsetHeight}px`,
        '--inset': `${this.element.offsetTop}px auto auto ${this.element.offsetLeft}px`,
      };
    });

    this.resizeObserver.observe(this.element);
  }

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with an "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {
    this.registerOnLinkClickHandler();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.cols
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  private registerOnLinkClickHandler() {
    this.element.querySelectorAll('[slot="content"] a[href]').forEach((link) => {
      link.addEventListener('click', () => {
        this.onImageGalleryLinkClicked.emit();
      });
    });
  }

  private openOverlay() {
    this.overlayIsOpen = true;

    this.onImageGalleryItemOpened.emit({
      closeCallback: this.closeOverlay.bind(this),
    });
  }

  private closeOverlay() {
    this.overlayIsOpen = false;

    this.onImageGalleryItemClosed.emit();
  }

  /**
   * render
   */
  render() {
    return (
      <Host
        onClick={() => this.openOverlay()}
        class={{ 'overlay-open': this.overlayIsOpen }}
        style={this.cssCustomProperties}
      >
        <slot name="image-mosaic" />

        <div class="overlay-container">
          <div class="overlay-image">
            <slot name="image-open" />
          </div>

          <slot name="content" />
        </div>
      </Host>
    );
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(ImageGalleryItemComponent.prototype, AbstractComponent.prototype);

export interface ImageGalleryItemComponent extends AbstractComponent {}
